import { createTheme } from "@mui/material/styles";

export const dayTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#667eea",
      light: "#8b9df7",
      dark: "#4c63d2",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#764ba2",
      light: "#9a6fc4",
      dark: "#5a3880",
      contrastText: "#ffffff",
    },
    accent: {
      main: "#14b8a6",
      light: "#2dd4bf",
      dark: "#0d9488",
    },
    error: {
      main: "#ef4444",
      light: "#f87171",
      dark: "#dc2626",
    },
    success: {
      main: "#10b981",
      light: "#34d399",
      dark: "#059669",
    },
    warning: {
      main: "#f59e0b",
      light: "#fbbf24",
      dark: "#d97706",
    },
    info: {
      main: "#3b82f6",
      light: "#60a5fa",
      dark: "#2563eb",
    },
    background: {
      default: "#f8fafc",
      paper: "#ffffff",
      topbar: "rgba(102, 126, 234, 0.95)",
      body: "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #14b8a6 100%)",
      cardBody: "rgba(255, 255, 255, 0.9)",
      cardBodyGlass: "rgba(255, 255, 255, 0.25)",
      blockButton: "#667eea",
      dialog: "rgba(255, 255, 255, 0.95)",
      logoBackground: "rgba(255, 255, 255, 0.15)",
      menuSetting: "rgba(102, 126, 234, 0.1)",
      staking: "#ffffff",
    },
  },
  typography: {
    fontFamily:
      '"Inter", "DM Sans", "AvenirNext-400", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    pageTitle: {
      fontSize: "clamp(36px, 5vw, 60px)",
      fontWeight: 700,
      color: "#ffffff",
      fontFamily: '"DM Sans", "AvenirNextSemiBold", sans-serif',
      letterSpacing: "-0.02em",
      lineHeight: 1.2,
    },
    title: {
      fontSize: "clamp(24px, 3vw, 32px)",
      fontWeight: 600,
      color: "#1e293b",
      fontFamily: '"DM Sans", "AvenirNextSemiBold", sans-serif',
      letterSpacing: "-0.01em",
    },
    h3: {
      fontSize: "24px",
      fontWeight: 600,
      color: "#334155",
      letterSpacing: "-0.01em",
    },
    h4: {
      fontSize: "20px",
      fontWeight: 600,
      color: "#475569",
    },
    body1: {
      fontSize: "16px",
      color: "#64748b",
      lineHeight: 1.6,
    },
    p: {
      fontSize: "16px",
      color: "#64748b",
      display: "block",
      lineHeight: 1.6,
    },
    span: {
      fontSize: "14px",
      color: "#64748b",
    },
    cardTitle: {
      fontSize: "22px",
      fontWeight: 600,
      color: "#1e293b",
      textAlign: "center",
      display: "block",
      letterSpacing: "-0.01em",
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: "none",
          fontWeight: 600,
          padding: "10px 24px",
          boxShadow: "none",
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(102, 126, 234, 0.3)",
            transform: "translateY(-2px)",
          },
        },
        contained: {
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.18)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
  },
  staking: {
    card: {
      background: "rgba(255, 255, 255, 0.9)",
      backdropFilter: "blur(10px)",
    },
    chartWrap: {
      background: "rgba(255, 255, 255, 0.95)",
    },
    line: "rgba(102, 126, 234, 0.3)",
    p: "#475569",
    background: "#f8fafc",
    button: {
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "#ffffff",
    },
    timeToggler: {
      background: "rgba(102, 126, 234, 0.1)",
    },
  },
});

export const nightTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#8b9df7",
      light: "#a5b4ff",
      dark: "#667eea",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#9a6fc4",
      light: "#b891d4",
      dark: "#764ba2",
      contrastText: "#ffffff",
    },
    accent: {
      main: "#14b8a6",
      light: "#f5a9fd",
      dark: "#d575e0",
    },
    error: {
      main: "#f87171",
      light: "#fca5a5",
      dark: "#ef4444",
    },
    success: {
      main: "#34d399",
      light: "#6ee7b7",
      dark: "#10b981",
    },
    warning: {
      main: "#fbbf24",
      light: "#fcd34d",
      dark: "#f59e0b",
    },
    info: {
      main: "#60a5fa",
      light: "#93c5fd",
      dark: "#3b82f6",
    },
    background: {
      default: "#0f172a",
      paper: "#1e293b",
      topbar: "rgba(30, 41, 59, 0.95)",
      body: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
      cardBody: "rgba(30, 41, 59, 0.9)",
      cardBodyGlass: "rgba(30, 41, 59, 0.5)",
      blockButton: "#8b9df7",
      dialog: "rgba(30, 41, 59, 0.95)",
      logoBackground: "rgba(139, 157, 247, 0.15)",
      menuSetting: "rgba(139, 157, 247, 0.1)",
      staking: "#1e293b",
    },
  },
  typography: {
    fontFamily:
      '"Inter", "DM Sans", "AvenirNext-400", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    pageTitle: {
      fontSize: "clamp(36px, 5vw, 60px)",
      fontWeight: 700,
      color: "#ffffff",
      fontFamily: '"DM Sans", "AvenirNextSemiBold", sans-serif',
      letterSpacing: "-0.02em",
      lineHeight: 1.2,
    },
    title: {
      fontSize: "clamp(24px, 3vw, 32px)",
      fontWeight: 600,
      color: "#f1f5f9",
      fontFamily: '"DM Sans", "AvenirNextSemiBold", sans-serif',
      letterSpacing: "-0.01em",
    },
    h3: {
      fontSize: "24px",
      fontWeight: 600,
      color: "#e2e8f0",
      letterSpacing: "-0.01em",
    },
    h4: {
      fontSize: "20px",
      fontWeight: 600,
      color: "#cbd5e1",
    },
    body1: {
      fontSize: "16px",
      color: "#94a3b8",
      lineHeight: 1.6,
    },
    p: {
      fontSize: "16px",
      color: "#94a3b8",
      display: "block",
      lineHeight: 1.6,
    },
    span: {
      fontSize: "14px",
      color: "#94a3b8",
    },
    cardTitle: {
      fontSize: "22px",
      fontWeight: 600,
      color: "#f1f5f9",
      textAlign: "center",
      display: "block",
      letterSpacing: "-0.01em",
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: "none",
          fontWeight: 600,
          padding: "10px 24px",
          boxShadow: "none",
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(139, 157, 247, 0.4)",
            transform: "translateY(-2px)",
          },
        },
        contained: {
          background: "linear-gradient(135deg, #8b9df7 0%, #9a6fc4 100%)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(139, 157, 247, 0.18)",
          background: "rgba(30, 41, 59, 0.8)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundImage: "none",
        },
      },
    },
  },
  staking: {
    card: {
      background: "rgba(30, 41, 59, 0.8)",
      backdropFilter: "blur(10px)",
    },
    chartWrap: {
      background: "rgba(30, 41, 59, 0.9)",
    },
    line: "rgba(139, 157, 247, 0.3)",
    p: "#94a3b8",
    background: "#0f172a",
    button: {
      background: "linear-gradient(135deg, #8b9df7 0%, #9a6fc4 100%)",
      color: "#ffffff",
    },
    timeToggler: {
      background: "rgba(139, 157, 247, 0.2)",
    },
  },
});
